import { useAuth } from '../state/auth-state';
import type { AdminRole } from '../state/auth-state';

/**
 * Authorization hook for role-based access control in the admin console.
 * Provides helpers to check role permissions and enforce access restrictions.
 *
 * Validates: Requirements 8.3, 8.4
 */

interface AuthorizationResult {
  /** The current user's role */
  readonly role: AdminRole | null;
  /** Whether the user is a System_Admin */
  readonly isSystemAdmin: boolean;
  /** Whether the user is a Domain_Admin */
  readonly isDomainAdmin: boolean;
  /** Whether the user has access to a given required role */
  readonly hasRole: (requiredRole: AdminRole) => boolean;
}

/**
 * Hook providing role-based authorization checks.
 * Extract role from the authenticated user state.
 */
export function useAuthorization(): AuthorizationResult {
  const { state } = useAuth();
  const role = state.user?.role ?? null;

  return {
    role,
    isSystemAdmin: role === 'System_Admin',
    isDomainAdmin: role === 'Domain_Admin',
    hasRole: (requiredRole: AdminRole): boolean => {
      if (!role) return false;
      // System_Admin has access to everything
      if (role === 'System_Admin') return true;
      // Domain_Admin only has access to Domain_Admin-level features
      return role === requiredRole;
    },
  };
}
